"use client";

import { FieldWrapper } from "@/components/form/FieldWrapper/FieldWrapper";
import clsx from "clsx";
import { useId } from "react";
import {
	FieldPath,
	FieldValues,
	RegisterOptions,
	UseFormRegister,
	UseFormRegisterReturn,
	useFormState,
} from "react-hook-form";
import { RadioInput } from "../../input/RadioInput/RadioInput";
import styles from "./RadioGroupField.module.scss";

type Option = {
	label: React.ReactNode;
	value: string;
};

export interface RadioGroupFieldProps<T extends FieldValues, TPath extends FieldPath<T>> {
	label: string;
	hideLabel?: boolean;
	hideError?: boolean;
	options?: Option[];
	className?: string;
	innerClassName?: string;
	optionClassName?: string;
	labelClassName?: string;
	inputClassName?: string;
	help?: string;
	register: UseFormRegister<T>;
	name: TPath;
	registerOptions?: RegisterOptions<T, TPath>;
	inputProps?: Omit<
		React.InputHTMLAttributes<HTMLInputElement>,
		keyof UseFormRegisterReturn<TPath>
	>;
	slotAfterInput?: React.ReactNode;
	ariaDescribedBy?: string;
}

export const RadioGroupField = <T extends FieldValues, TPath extends FieldPath<T>>({
	name,
	label,
	hideLabel,
	hideError,
	options,
	className,
	innerClassName,
	optionClassName,
	labelClassName,
	inputClassName,
	help,
	register,
	registerOptions,
	inputProps,
	slotAfterInput,
	ariaDescribedBy,
}: RadioGroupFieldProps<T, TPath>) => {
	const id = useId();
	const helpId = id + "-help";
	const errorId = id + "-error";

	const { errors } = useFormState();

	return (
		<FieldWrapper
			{...{
				name,
				label,
				hideLabel,
				hideError,
				required: registerOptions?.required as boolean,
				labelId: id,
				className,
				errorId,
			}}
		>
			<div className={clsx(styles["radio-group-wrapper"])}>
				<div
					className={clsx(
						styles["radio-group"],
						innerClassName,
						hideLabel && styles["radio-group--hidden-label"],
					)}
					role="radiogroup"
					aria-labelledby={id}
				>
					{options?.map(radio => (
						<RadioInput
							value={radio.value}
							key={radio.value}
							label={radio.label}
							className={optionClassName}
							labelClassName={labelClassName}
							inputClassName={inputClassName}
							aria-describedby={
								[help ? helpId : "", errors[name] ? errorId : "", ariaDescribedBy]
									.filter(v => v)
									.join(" ") || undefined
							}
							{...register(name, registerOptions)}
							{...inputProps}
						/>
					))}
				</div>
				{slotAfterInput}
			</div>
		</FieldWrapper>
	);
};
